import { useEffect, useRef, useCallback } from 'react';

interface UseScrollSpyOptions {
  /** The scrollable container element ref. If null, observer uses viewport. */
  scrollRef: React.RefObject<HTMLElement | null>;
  /** Ref to the pill navigation bar (for auto-scroll of active pill). */
  navRef: React.RefObject<HTMLElement | null>;
  /** Category IDs to observe. */
  categoryIds: string[];
  /** Prefix for element IDs, e.g. 'spd-cat-' → looks for id="spd-cat-{catId}" */
  idPrefix: string;
  /** Whether the spy is active. */
  enabled: boolean;
  /** Callback when active category changes. */
  onActiveCatChange: (catId: string) => void;
}

/**
 * Reusable ScrollSpy hook — uses scroll events to detect which category
 * header is nearest the top of the scrollable container, auto-highlights the
 * corresponding pill, and auto-scrolls the pill bar.
 */
export function useScrollSpy({
  scrollRef,
  navRef,
  categoryIds,
  idPrefix,
  enabled,
  onActiveCatChange,
}: UseScrollSpyOptions) {
  // Keep callback ref fresh so scroll handler never captures stale closure
  const cbRef = useRef(onActiveCatChange);
  cbRef.current = onActiveCatChange;

  const idsRef = useRef(categoryIds);
  idsRef.current = categoryIds;

  const prefixRef = useRef(idPrefix);
  prefixRef.current = idPrefix;

  useEffect(() => {
    if (!enabled || !categoryIds.length) return;
    const root = scrollRef.current;
    if (!root) return;

    let rafId = 0;

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const ids = idsRef.current;
        const prefix = prefixRef.current;
        const rootRect = root.getBoundingClientRect();
        const threshold = rootRect.top + 80; // 80px below container top

        let bestId: string | null = null;
        let bestDist = Infinity;

        for (const id of ids) {
          const el = document.getElementById(`${prefix}${id}`);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          // Category whose top is at or above threshold and closest to it
          if (rect.top <= threshold) {
            const dist = threshold - rect.top;
            if (dist < bestDist) {
              bestDist = dist;
              bestId = id;
            }
          }
        }

        // If no category is at/above threshold, pick first visible
        if (!bestId) {
          for (const id of ids) {
            const el = document.getElementById(`${prefix}${id}`);
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            if (rect.bottom > rootRect.top && rect.top < rootRect.bottom) {
              bestId = id;
              break;
            }
          }
        }

        if (bestId) cbRef.current(bestId);
      });
    };

    root.addEventListener('scroll', handleScroll, { passive: true });
    // Run once initially to set active pill
    handleScroll();

    return () => {
      root.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, categoryIds.join(','), idPrefix, scrollRef.current]);

  // Auto-scroll the active pill into view in the navigation bar
  const scrollPillIntoView = useCallback((activeCat: string) => {
    const nav = navRef.current;
    if (!nav || !activeCat) return;
    const pill = nav.querySelector(`[data-pill="${activeCat}"]`) as HTMLElement | null;
    if (pill) pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [navRef]);

  return { scrollPillIntoView };
}
